import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { resolveTrackedPageKey } from '../utils/pageViewTracking';

/** Dedupe rapid remounts / React Strict Mode (refs reset on remount — sessionStorage survives). */
const DEDupe_MS = 2500;
const storageKey = (pageKey) => `pxc_pv_${pageKey}`;

/**
 * Records lightweight page-view events for admin analytics.
 * Fire-and-forget; failures are silent so navigation is never blocked.
 */
export default function PageViewTracker() {
  const location = useLocation();
  const lastKeyRef = useRef(null);
  const lastAtRef = useRef(0);

  useEffect(() => {
    const pageKey = resolveTrackedPageKey(location.pathname);
    if (!pageKey) return;

    const now = Date.now();
    // In-memory: same path re-renders without remount (parent updates)
    if (lastKeyRef.current === pageKey && now - lastAtRef.current < DEDupe_MS) {
      return;
    }
    // sessionStorage: survives Strict Mode unmount→remount in dev (refs do not)
    try {
      const sk = storageKey(pageKey);
      const prev = Number(sessionStorage.getItem(sk) || '0');
      if (now - prev < DEDupe_MS) {
        lastKeyRef.current = pageKey;
        lastAtRef.current = now;
        return;
      }
      sessionStorage.setItem(sk, String(now));
    } catch (_) {
      /* private mode / blocked storage — fall through */
    }
    lastKeyRef.current = pageKey;
    lastAtRef.current = now;

    const uid = auth.currentUser?.uid ?? null;
    addDoc(collection(db, 'pageViewEvents'), {
      pageKey,
      path: location.pathname.slice(0, 200),
      uid,
      createdAt: serverTimestamp(),
    }).catch(() => {
      /* non-blocking */
    });
  }, [location.pathname]);

  return null;
}
